import { INode, MaskInputOptions, BlockElement } from '@xysfe/memento-snapshot';
import * as tool from '../utils' 
import {
  mirror,
  throttle,
  on,
  hookSetter,
  getWindowHeight,
  getWindowWidth,
  isBlocked,
  isTouchEvent,
} from '../utils';
import {
  mutationCallBack,
  observerParam,
  mousemoveCallBack,
  mousePosition,
  mouseInteractionCallBack,
  MouseInteractions,
  listenerHandler,
  scrollCallback,
  styleSheetRuleCallback,
  viewportResizeCallback,
  inputValue,
  inputCallback,
  hookResetter,
  blockClass,
  IncrementalSource,
  hooksParam,
  Arguments,
  mediaInteractionCallback,
  MediaInteractions,
  SamplingStrategy,
  asyncClass,
  UserDefinedEvent,
  userDefinedEventCallback
} from '../types';
import MutationBuffer from './mutation';

const mql = window.matchMedia('(orientation: portrait)');
let isPortrait = mql.matches;

function initMutationObserver(
  cb: mutationCallBack,
  blockClass: blockClass,
  inlineStylesheet: boolean,
  maskInputOptions: MaskInputOptions,
  blockElements: BlockElement[] = [],
  asyncClass?: asyncClass,
): MutationObserver {
  // see mutation.ts for details
  const mutationBuffer = new MutationBuffer(
    cb,
    blockClass,
    inlineStylesheet,
    maskInputOptions,
    blockElements,
    asyncClass,
  );
  const observer = new MutationObserver(mutationBuffer.processMutations);
  observer.observe(document, {
    attributes: true,
    attributeOldValue: true,
    characterData: true,
    characterDataOldValue: true,
    childList: true,
    subtree: true,
  });
  return observer;
}

function initMoveObserver(
  cb: mousemoveCallBack,
  sampling: SamplingStrategy,
): listenerHandler {
  if (sampling.mousemove === false) {
    return () => {};
  }

  const threshold =
    typeof sampling.mousemove === 'number' ? sampling.mousemove : 50;

  let positions: mousePosition[] = [];
  let timeBaseline: number | null;
  const wrappedCb = throttle((isTouch: boolean) => {
    const totalOffset = Date.now() - timeBaseline!;
    cb(
      positions.map((p) => {
        p.timeOffset -= totalOffset;
        return p;
      }),
      isTouch ? IncrementalSource.TouchMove : IncrementalSource.MouseMove,
    );
    positions = [];
    timeBaseline = null;
  }, 500);
  const updatePosition = throttle<MouseEvent | TouchEvent>(
    (evt) => {
      const { target } = evt;
      const { clientX, clientY } = isTouchEvent(evt)
        ? evt.changedTouches[0]
        : evt;
      if (!timeBaseline) {
        timeBaseline = Date.now();
      }
      positions.push({
        x: clientX,
        y: clientY,
        id: mirror.getId(target as INode),
        timeOffset: Date.now() - timeBaseline,
      });
      wrappedCb(isTouchEvent(evt));
    },
    threshold,
    {
      trailing: false,
    },
  );
  const handlers = [
    on('mousemove', updatePosition),
    on('touchmove', updatePosition),
  ];
  return () => {
    handlers.forEach((h) => h());
  };
}

function initMouseInteractionObserver(
  cb: mouseInteractionCallBack,
  blockClass: blockClass,
  sampling: SamplingStrategy,
  blockElements: BlockElement[] = [],
): listenerHandler {
  if (sampling.mouseInteraction === false) {
    return () => {};
  }
  const disableMap: Record<string, boolean | undefined> =
    sampling.mouseInteraction === true ||
    sampling.mouseInteraction === undefined
      ? {}
      : sampling.mouseInteraction;

  const handlers: listenerHandler[] = [];
  const getHandler = (eventKey: keyof typeof MouseInteractions) => {
    return (event: MouseEvent | TouchEvent) => {
      if (isBlocked(event.target as Node, blockClass, blockElements)) {
        return;
      }
      const id = mirror.getId(event.target as INode);
      const { clientX, clientY } = isTouchEvent(event)
        ? event.changedTouches[0]
        : event;
      cb({
        type: MouseInteractions[eventKey],
        id,
        x: clientX,
        y: clientY,
      });
    };
  };
  Object.keys(MouseInteractions)
    .filter(
      (key) =>
        Number.isNaN(Number(key)) &&
        !key.endsWith('_Departed') &&
        disableMap[key] !== false,
    )
    .forEach((eventKey: keyof typeof MouseInteractions) => {
      const eventName = eventKey.toLowerCase();
      const handler = getHandler(eventKey);
      handlers.push(on(eventName, handler));
    });
  return () => {
    handlers.forEach((h) => h());
  };
}

function initScrollObserver(
  cb: scrollCallback,
  blockClass: blockClass,
  sampling: SamplingStrategy,
  blockElements: BlockElement[] = [],
): listenerHandler {
  const updatePosition = throttle<UIEvent>((evt) => {
    if (!evt.target || isBlocked(evt.target as Node, blockClass, blockElements)) {
      return;
    }
    const id = mirror.getId(evt.target as INode);
    if (evt.target === document) {
      const scrollEl = (document.scrollingElement || document.documentElement)!;
      cb({
        id,
        x: scrollEl.scrollLeft,
        y: scrollEl.scrollTop,
      });
    } else {
      cb({
        id,
        x: (evt.target as HTMLElement).scrollLeft,
        y: (evt.target as HTMLElement).scrollTop,
      });
    }
  }, sampling.scroll || 100);
  return on('scroll', updatePosition);
}

function initViewportResizeObserver(
  cb: viewportResizeCallback,
): listenerHandler {
  const updateDimension = throttle(() => {
    const height = getWindowHeight();
    const width = getWindowWidth();
    cb({
      width: Number(width),
      height: Number(height),
      rotate: isPortrait !== mql.matches ? 1 : 0,
    });
    isPortrait = mql.matches;
  }, 200);
  return on('resize', updateDimension, window);
}

export const INPUT_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];
const lastInputValueMap: WeakMap<EventTarget, inputValue> = new WeakMap();
function initInputObserver(
  cb: inputCallback,
  blockClass: blockClass,
  ignoreClass: string,
  maskInputOptions: MaskInputOptions,
  sampling: SamplingStrategy,
  blockElements: BlockElement[] = [],
): listenerHandler {
  function eventHandler(event: Event) {
    const { target } = event;
    if (
      !target ||
      !(target as Element).tagName ||
      INPUT_TAGS.indexOf((target as Element).tagName) < 0 ||
      isBlocked(target as Node, blockClass, blockElements)
    ) {
      return;
    }
    const type: string | undefined = (target as HTMLInputElement).type;
    if (
      type === 'password' ||
      (target as HTMLElement).classList.contains(ignoreClass)
    ) {
      return;
    }
    let text = (target as HTMLInputElement).value;
    let isChecked = false;
    if (type === 'radio' || type === 'checkbox') {
      isChecked = (target as HTMLInputElement).checked;
    } else if (
      maskInputOptions[
        (target as Element).tagName.toLowerCase() as keyof MaskInputOptions
      ] ||
      maskInputOptions[type as keyof MaskInputOptions]
    ) {
      text = '*'.repeat(text.length);
    }
    cbWithDedup(target, { text, isChecked });
    // if a radio was checked
    // the other radios with the same name attribute will be unchecked.
    const name: string | undefined = (target as HTMLInputElement).name;
    if (type === 'radio' && name && isChecked) {
      document
        .querySelectorAll(`input[type="radio"][name="${name}"]`)
        .forEach((el) => {
          if (el !== target) {
            cbWithDedup(el, {
              text: (el as HTMLInputElement).value,
              isChecked: !isChecked,
            });
          }
        });
    }
  }
  function cbWithDedup(target: EventTarget, v: inputValue) {
    const lastInputValue = lastInputValueMap.get(target);
    if (
      !lastInputValue ||
      lastInputValue.text !== v.text ||
      lastInputValue.isChecked !== v.isChecked
    ) {
      lastInputValueMap.set(target, v);
      const id = mirror.getId(target as INode);
      cb({
        ...v,
        id,
      });
    }
  }
  const events = sampling.input === 'last' ? ['change'] : ['input', 'change'];
  const handlers: Array<
    listenerHandler | hookResetter
  > = events.map((eventName) => on(eventName, eventHandler));
  const propertyDescriptor = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value',
  );
  const hookProperties: Array<[HTMLElement, string]> = [
    [HTMLInputElement.prototype, 'value'],
    [HTMLInputElement.prototype, 'checked'],
    [HTMLSelectElement.prototype, 'value'],
    [HTMLTextAreaElement.prototype, 'value'],
  ];
  if (propertyDescriptor && propertyDescriptor.set) {
    handlers.push(
      ...hookProperties.map((p) =>
        hookSetter<HTMLElement>(p[0], p[1], {
          set() {
            // mock to a normal event
            eventHandler({ target: this } as Event);
          },
        }),
      ),
    );
  }
  return () => {
    handlers.forEach((h) => h());
  };
}

function initStyleSheetObserver(cb: styleSheetRuleCallback): listenerHandler {
  const insertRule = CSSStyleSheet.prototype.insertRule;
  CSSStyleSheet.prototype.insertRule = function (rule: string, index?: number) {
    const id = mirror.getId(this.ownerNode as INode);
    if (id !== -1) {
      cb({
        id,
        adds: [{ rule, index }],
      });
    }
    return insertRule.apply(this, arguments);
  };

  const deleteRule = CSSStyleSheet.prototype.deleteRule;
  CSSStyleSheet.prototype.deleteRule = function (index: number) {
    const id = mirror.getId(this.ownerNode as INode);
    if (id !== -1) {
      cb({
        id,
        removes: [{ index }],
      });
    }
    return deleteRule.apply(this, arguments);
  };

  return () => {
    CSSStyleSheet.prototype.insertRule = insertRule;
    CSSStyleSheet.prototype.deleteRule = deleteRule;
  };
}

function initMediaInteractionObserver(
  mediaInteractionCb: mediaInteractionCallback,
  blockClass: blockClass,
  blockElements: BlockElement[] = [],
): listenerHandler {
  const handler = (type: 'play' | 'pause') => (event: Event) => {
    const { target } = event;
    if (!target || isBlocked(target as Node, blockClass, blockElements)) {
      return;
    }
    mediaInteractionCb({
      type: type === 'play' ? MediaInteractions.Play : MediaInteractions.Pause,
      id: mirror.getId(target as INode),
    });
  };
  const handlers = [on('play', handler('play')), on('pause', handler('pause'))];
  return () => {
    handlers.forEach((h) => h());
  };
}

function initUserDefinedEventObserver(userDefinedEventCb: userDefinedEventCallback) {
  // const updateDimension = throttle(() => {
  //   const height = getWindowHeight();
  //   const width = getWindowWidth();
  //   cb({
  //     width: Number(width),
  //     height: Number(height),
  //     rotate: isPortrait !== mql.matches ? 1 : 0,
  //   });
  //   isPortrait = mql.matches;
  // }, 200);
  // return on('resize', updateDimension, window);

  const handler = (evt: UserDefinedEvent) => (event: Event) => {
    if ('detail' in event && event.detail) {
      const { detail } = event as CustomEvent;
      userDefinedEventCb({
        evt,
        ...detail
      });
    }
  };

  const handlers = [
    on('memento-live-play', handler(UserDefinedEvent.LivePlay))
  ];
  return () => {
    handlers.forEach(h => h());
  };
}

function initConsoleObserver(cb: any): listenerHandler {
  let _console: any = {}
  const resource = ['script', 'img', 'link', 'video', 'audio']
  const methodList = ['log', 'info', 'warn', 'error']
  const resourceIgnoreList: any = []
  if (!window.console) {
    (window.console as any) = {}
  } else {
    methodList.map(function (method) {
      _console[method] = (window.console as any)[method]
    })
  }
  methodList.map((method) => {
    (window.console as any)[method] = (...args: any) => {
      _console[method].apply(window.console, args)
      let logs = args
      for (let i = 0; i < logs.length; i++) {
        if (tool.isArray(logs[i])) {
          logs[i] = '[Array]'
        } else if (tool.isElement(logs[i])) {
          logs[i] = '[HTMLElement]'
        } else if (tool.isFunction(logs[i])) {
          logs[i] = '[Function]'
        } else if (tool.isWindow(logs[i])) {
          logs[i] = '[Window]'
        } else if (tool.isObject(logs[i])) {
          logs[i] = '[Object]'
        }
      }
      args[0] !== '__memento__' && cb({
        logType: method,
        logs: logs
      })
    }
  })
  // 资源请求错误
  window.addEventListener('error', function (e) {
    let target: any = e.target || e.srcElement
    let src = target.attributes && target.attributes.src && target.attributes.src.value || ''
    if (((target.nodeName && resource.indexOf(target.nodeName.toLowerCase()) >= 0) || (target.tagName && resource.indexOf(target.tagName.toLowerCase()) >= 0)) && src && resourceIgnoreList.indexOf(src.toLowerCase()) < 0) {
      resourceIgnoreList.push(src.toLowerCase())
      const msg = (target.nodeName || target.tagName || '') + ' ' + (src || 'unkonw') + ' loaded fail'
      cb({
        logType: 'resourceError',
        logs: [msg]
      })
    }
  })

  // 收集js错误
  const _onerror: any = window.onerror
  window.onerror = function (msg: string, url: string, lineNo: any, columnNo: any, error: any) {
    if (msg && !(msg.indexOf('WeixinJSBridge') >= 0)) {
      cb({
        logType: 'jsError',
        logs: [
          `pageUrl => ${window.location.href}`,
          `errorMsg => ${msg || ''}`, `scriptUrl => ${url || ''}`,
          `lineNo => ${lineNo}`, `columnNo => ${columnNo}`,
          `errorType => ${error ? error.name : ''}`,
          `errorStack => ${error ? error.stack : ''}`
        ]
      })
    }
    return _onerror.apply(this, arguments)
  }

  return () => {
    window.console.log = _console.log
    window.console.info = _console.info
    window.console.warn = _console.warn
    // window.console.debug = _console.debug
    window.console.error = _console.error
    window.onerror = _onerror
  }
}

function initNetworkObserver(cb: any): listenerHandler {
  let _XMLHttpRequest = window.XMLHttpRequest
  if (!_XMLHttpRequest) { return () => {} }
  let _mockopen = window.XMLHttpRequest.prototype.open
  let _mocksend = window.XMLHttpRequest.prototype.send
  let _mockSetRequestHeader = window.XMLHttpRequest.prototype.setRequestHeader
  var reqList: any = {}

  window.XMLHttpRequest.prototype.setRequestHeader = function (header, value) {
    var XMLReq = this
    var headerLower = header.toLowerCase()
    if (headerLower === 'memento-ignore') {
        XMLReq._noMementoConsole = ~~value === 1
    }
    return _mockSetRequestHeader.apply(XMLReq, arguments)
  }
  window.XMLHttpRequest.prototype.open = function() { 
    let XMLReq = this
    let args = [].slice.call(arguments),
        method = args[0],
        url = args[1],
        id = getUniqueID()
    let timer: any = null

    XMLReq._mockRequestID = id
    XMLReq._mockMethod = method
    XMLReq._mockUrl = url
    let _mocknoMementoConsole = XMLReq.onreadystatechange || function() {}
    let onreadystatechange = function() {

      let item = reqList[id] || {}
      item.readyState = XMLReq.readyState
      item.status = 0
      if (XMLReq.readyState > 1) {
        item.status = XMLReq.status
      }
      item.responseType = XMLReq.responseType

      if (XMLReq.readyState == 0) {
        // UNSENT
        if (!item.startTime) {
          item.startTime = (+new Date())
        }
      } else if (XMLReq.readyState == 1) {
        // OPENED
        if (!item.startTime) {
          item.startTime = (+new Date())
        }
      } else if (XMLReq.readyState == 2) {
        // HEADERS_RECEIVED
      } else if (XMLReq.readyState == 3) {
        // LOADING
      } else if (XMLReq.readyState == 4) {
        // DONE
        clearInterval(timer)
        let query = XMLReq._mockUrl.split('?')
        item.url = query.shift() || XMLReq._mockUrl
        item.endTime = +new Date()
        item.costTime = item.endTime - (item.startTime || item.endTime)
        item.ua = (window.navigator && window.navigator.userAgent) || ''
        item.response = XMLReq.response
        if (typeof XMLReq.response === 'string') {
          item.responseSize = XMLReq.response.length
        } else if (XMLReq.response && typeof (XMLReq.response as any).size === 'number') {
          item.responseSize = (XMLReq.response as any).size
        } else if (XMLReq.response && typeof (XMLReq.response as any).byteLength === 'number') {
          item.responseSize = (XMLReq.response as any).byteLength
        } else {
          try {
            item.responseSize = JSON.stringify(XMLReq.response).length
          } catch (e) {
            item.responseSize = 0
          }
        }
      } else {
        clearInterval(timer)
      }

      if (!XMLReq._noMementoConsole) {
        updateRequest(id, item)
      }

      return _mocknoMementoConsole.apply(XMLReq, arguments)
    };
    XMLReq.onreadystatechange = onreadystatechange

    let preState = -1
    timer = setInterval(function() {
      if (preState != XMLReq.readyState) {
        preState = XMLReq.readyState
        onreadystatechange.call(XMLReq)
      }
    }, 10);

    return _mockopen.apply(XMLReq, args)
  }

  window.XMLHttpRequest.prototype.send = function() {
    let XMLReq = this
    let args = [].slice.call(arguments),
        data = args[0]

    let item = reqList[XMLReq._mockRequestID] || {}
    item.method = XMLReq._mockMethod.toUpperCase()
    item.ck = document.cookie

    let query = XMLReq._mockUrl.split('?')
    // item.url = query.shift() || XMLReq._mockUrl

    if (query.shift() && query.length > 0) {
      item.getData = {};
      query = query.join('?')
      query = query.split('&')
      for (let q of query) {
        q = q.split('=')
        item.getData[ q[0] ] = decodeURIComponent(q[1])
      }
      item.getData && (item.getSize = JSON.stringify(item.getData).length)
    }

    if (item.method == 'POST') {
      // save POST data
      if (tool.isString(data) && data !== '') {
        let arr = data.split('&')
        item.postData = {}
        for (let q of arr) {
          q = q.split('=')
          item.postData[ decodeURIComponent(q[0]) ] = decodeURIComponent(q[1])
        }
      } else if (tool.isPlainObject(data)) {
        item.postData = data
      }
      item.postData && (item.requestSize = JSON.stringify(item.postData).length)
    }

    let _mockontimeout = XMLReq.ontimeout || function() {}
    let ontimeout = function () {
      let target: any = arguments[0].target || arguments[0].srcElement
      item.timeOut = target.timeout 
      item.errorType = 'timeout_error'
      updateRequest(XMLReq._mockRequestID, item)
      return _mockontimeout.apply(XMLReq, arguments)
    }
    XMLReq.ontimeout = ontimeout

    if (!XMLReq._noMementoConsole) {
      updateRequest(XMLReq._mockRequestID, item)
    }

    return _mocksend.apply(XMLReq, args)
  }

  function updateRequest (id: any, data: any) {
    let item = reqList[id] || {}
    for (let key in data) {
      item[key] = data[key]
    }
    reqList[id] = item
    if (!item.hasOwnProperty('getData') && !item.hasOwnProperty('postData')) {
      delete reqList[id]
    }
    if (item && item.requestSize && item.requestSize > 10000) {
      item.postData = 'Post Request => Too long has been blocked'
    }
    if (item && item.getSize && item.getSize > 10000) {
      item.getData = 'Get Request => Too long has been blocked'
    }
    if (item && item.responseSize && item.responseSize > 30 * 1024 * 1024) {
      item.response = 'Post Request => Too long has been blocked'
    } else if (item && (item.responseType === '' || item.responseType === 'text')) {
      if (tool.isString(item.response) && item.response !== '') {
        try {
          item.response = JSON.parse(item.response)
        } catch (e) {
          item.response = item.response
        }
      } else if (typeof item.response != 'undefined') {
        if (item.response === '') {
          item.response = ''
        } else {
          item.response = Object.prototype.toString.call(item.response)
        }
      }
    }
    if (item.readyState && item.readyState === 4) {
      if (item && +item.status === 0) {
        if (item.errorType === 'timeout_error') {
          item.errorMsg = 'request error => ' + 'timeout ( responseTime > ' + (item.timeOut || item.costTime) + 'ms )'
          delete item.timeOut
        } else {
          item.errorMsg = 'request error => cross_domain_error'
          item.errorType = 'cross_domain_error'
        }
        cb(item)
      } else {
        cb(item || {})
      }
    }
  }
  function getUniqueID () {
    let id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      let r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8)
      return v.toString(16)
    });
    return id
  }

  return () => {
    window.XMLHttpRequest.prototype.setRequestHeader = _mockSetRequestHeader
    window.XMLHttpRequest.prototype.open = _mockopen
    window.XMLHttpRequest.prototype.send = _mocksend
  }
}

function mergeHooks(o: observerParam, hooks: hooksParam) {
  const {
    mutationCb,
    mousemoveCb,
    mouseInteractionCb,
    scrollCb,
    viewportResizeCb,
    inputCb,
    mediaInteractionCb,
    styleSheetRuleCb,
  } = o;
  o.mutationCb = (...p: Arguments<mutationCallBack>) => {
    if (hooks.mutation) {
      hooks.mutation(...p);
    }
    mutationCb(...p);
  };
  o.mousemoveCb = (...p: Arguments<mousemoveCallBack>) => {
    if (hooks.mousemove) {
      hooks.mousemove(...p);
    }
    mousemoveCb(...p);
  };
  o.mouseInteractionCb = (...p: Arguments<mouseInteractionCallBack>) => {
    if (hooks.mouseInteraction) {
      hooks.mouseInteraction(...p);
    }
    mouseInteractionCb(...p);
  };
  o.scrollCb = (...p: Arguments<scrollCallback>) => {
    if (hooks.scroll) {
      hooks.scroll(...p);
    }
    scrollCb(...p);
  };
  o.viewportResizeCb = (...p: Arguments<viewportResizeCallback>) => {
    if (hooks.viewportResize) {
      hooks.viewportResize(...p);
    }
    viewportResizeCb(...p);
  };
  o.inputCb = (...p: Arguments<inputCallback>) => {
    if (hooks.input) {
      hooks.input(...p);
    }
    inputCb(...p);
  };
  o.mediaInteractionCb = (...p: Arguments<mediaInteractionCallback>) => {
    if (hooks.mediaInteaction) {
      hooks.mediaInteaction(...p);
    }
    mediaInteractionCb(...p);
  };
  o.styleSheetRuleCb = (...p: Arguments<styleSheetRuleCallback>) => {
    if (hooks.styleSheetRule) {
      hooks.styleSheetRule(...p);
    }
    styleSheetRuleCb(...p);
  };
}

export default function initObservers(
  o: observerParam,
  hooks: hooksParam = {},
): listenerHandler {
  mergeHooks(o, hooks);
  const mutationObserver = initMutationObserver(
    o.mutationCb,
    o.blockClass,
    o.inlineStylesheet,
    o.maskInputOptions,
    o.blockElements,
    o.asyncClass,
  );
  const mousemoveHandler = initMoveObserver(o.mousemoveCb, o.sampling);
  const mouseInteractionHandler = initMouseInteractionObserver(
    o.mouseInteractionCb,
    o.blockClass,
    o.sampling,
    o.blockElements,
  );
  const scrollHandler = initScrollObserver(
    o.scrollCb,
    o.blockClass,
    o.sampling,
    o.blockElements,
  );
  const viewportResizeHandler = initViewportResizeObserver(o.viewportResizeCb);
  const inputHandler = initInputObserver(
    o.inputCb,
    o.blockClass,
    o.ignoreClass,
    o.maskInputOptions,
    o.sampling,
  );
  const mediaInteractionHandler = initMediaInteractionObserver(
    o.mediaInteractionCb,
    o.blockClass,
    o.blockElements,
  );
  const styleSheetObserver = initStyleSheetObserver(o.styleSheetRuleCb);
  const userDefinedEventHandler = initUserDefinedEventObserver(o.userDefinedEventCb)

  return () => {
    mutationObserver.disconnect();
    mousemoveHandler();
    mouseInteractionHandler();
    scrollHandler();
    viewportResizeHandler();
    inputHandler();
    // mediaInteractionHandler();
    styleSheetObserver();
    userDefinedEventHandler()
  };
}

function mergeOtherHooks (o: any, hooks: any = {}) {
  const {
    consoleCb,
    networkCb
  } = o
  o.consoleCb = (...p: any) => {
    if (hooks.console) {
      hooks.console(...p);
    }
    consoleCb(...p);
  };
  o.networkCb = (...p: any) => {
    if (hooks.network) {
      hooks.network(...p);
    }
    networkCb(...p);
  };
}

export function initOtherObserver (o: any, hooks: any): listenerHandler {
  mergeOtherHooks(o, hooks)
  const consoleObserver = initConsoleObserver(o.consoleCb)
  const networkObserver = initNetworkObserver(o.networkCb)
  return () => {
    consoleObserver()
    networkObserver()
  }
}
